/**
 * Renter Onboarding — 3 steps:
 * 1. Student vs Professional card picker
 * 2. VibeMate lifestyle quiz (6 icon-based questions)
 * 3. Profile ready screen with animated pts counter
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { VIBE_TYPE_AVATARS } from '../data/vibeAvatars';

const API   = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

// ── Quiz data ─────────────────────────────────────────────────────────────────
const QUIZ = [
  {
    q: 'What\u2019s your sleep schedule like?',
    opts: [
      { icon: '🌅', label: 'Early bird',  sub: 'Up before 7am' },
      { icon: '🌙', label: 'Night owl',   sub: 'Up past midnight' },
      { icon: '🔄', label: 'Flexible',    sub: 'Depends on the day' },
    ],
  },
  {
    q: 'Where do you prefer to study or work?',
    opts: [
      { icon: '🏠', label: 'At home',        sub: 'Need quiet space' },
      { icon: '☕', label: 'Outside',         sub: 'Cafes / library' },
      { icon: '🎧', label: 'Either — headphones on', sub: 'Adaptable' },
    ],
  },
  {
    q: 'How social are you at home?',
    opts: [
      { icon: '🎉', label: 'Love having people over', sub: 'Regular gatherings' },
      { icon: '🤝', label: 'Occasionally',             sub: 'Small groups sometimes' },
      { icon: '🚪', label: 'Keep to myself',           sub: 'Home is my sanctuary' },
    ],
  },
  {
    q: 'How would you describe your cleanliness?',
    opts: [
      { icon: '✨', label: 'Very tidy',       sub: 'Everything has a place' },
      { icon: '🙂', label: 'Reasonably clean', sub: 'Tidy enough' },
      { icon: '😌', label: 'Relaxed',          sub: 'Lived-in feels fine' },
    ],
  },
  {
    q: 'What\u2019s your noise tolerance?',
    opts: [
      { icon: '🔇', label: 'Quiet please',          sub: 'I need silence to focus' },
      { icon: '🎵', label: 'Some background noise',  sub: 'Music or TV is fine' },
      { icon: '🔊', label: 'Lively is fine',         sub: 'Energy keeps me going' },
    ],
  },
  {
    q: 'How often do you have guests over?',
    opts: [
      { icon: '👥', label: 'Often',          sub: 'Friends stay regularly' },
      { icon: '🗓️', label: 'Occasionally',   sub: 'Once or twice a month' },
      { icon: '🙅', label: 'Rarely',         sub: 'Almost never' },
    ],
  },
];

const VIBE_LABELS = {
  social_anchor:      'The Social Anchor',
  quiet_professional: 'The Quiet Professional',
  night_owl:          'The Night Owl Scholar',
  early_riser:        'The Early Riser',
  free_spirit:        'The Free Spirit',
  homebody:           'The Homebody',
};

export default function OnboardingRenter() {
  const [step, setStep]           = useState(1);   // 1 = type, 2 = quiz, 3 = done
  const [userType, setUserType]   = useState('');
  const [quizStep, setQuizStep]   = useState(0);
  const [answers, setAnswers]     = useState([]);
  const [vibeType, setVibeType]   = useState('');
  const [ptsEarned, setPtsEarned] = useState(0);
  const [animPts, setAnimPts]     = useState(0);
  const [saving, setSaving]       = useState(false);

  const { token }  = useAuth();
  const navigate   = useNavigate();

  // Animate points counter on step 3
  useEffect(() => {
    if (step !== 3 || ptsEarned === 0) return;
    let current = 0;
    const step_ = Math.ceil(ptsEarned / 30);
    const timer = setInterval(() => {
      current = Math.min(current + step_, ptsEarned);
      setAnimPts(current);
      if (current >= ptsEarned) clearInterval(timer);
    }, 40);
    return () => clearInterval(timer);
  }, [step, ptsEarned]);

  const handleAnswer = async (answerIdx) => {
    const newAnswers = [...answers, answerIdx];
    setAnswers(newAnswers);

    if (quizStep < QUIZ.length - 1) {
      setQuizStep(q => q + 1);
      return;
    }

    // Quiz done — submit to backend
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/onboarding/renter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_type: userType, quiz_answers: newAnswers }),
      });
      const data = await res.json();
      setVibeType(data.vibe_type || 'free_spirit');
      setPtsEarned(data.points_awarded || 375);
      setStep(3);
    } catch {
      setVibeType('free_spirit');
      setPtsEarned(375);
      setStep(3);
    } finally { setSaving(false); }
  };

  const progress = ((quizStep) / QUIZ.length) * 100;

  // ── Step 1: User type ─────────────────────────────────────────────────────
  if (step === 1) return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#f0f7fa,#e8f4f8)' }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#2cbdc4]/10 border border-[#2cbdc4]/30 px-4 py-1.5 rounded-full text-xs font-bold text-[#094f6f] uppercase tracking-wide mb-4">
            Step 1 of 3 — Tell us about you
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">I am a…</h1>
          <p className="text-gray-500 text-sm">This helps us personalise your experience and pricing</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-8">
          {[
            { id: 'student',      icon: '🎓', title: 'Student',             sub: 'Enrolled at a university or TAFE' },
            { id: 'professional', icon: '💼', title: 'Working Professional', sub: 'Employed or self-employed' },
          ].map(opt => (
            <button key={opt.id} onClick={() => { setUserType(opt.id); setStep(2); }}
              className="flex flex-col items-center gap-3 p-8 bg-white rounded-3xl border-2 border-gray-200 hover:border-[#2cbdc4] hover:shadow-lg transition-all group">
              <span className="text-5xl">{opt.icon}</span>
              <p className="font-black text-gray-900 text-lg group-hover:text-[#094f6f]">{opt.title}</p>
              <p className="text-xs text-gray-400 text-center">{opt.sub}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Step 2: Quiz ──────────────────────────────────────────────────────────
  if (step === 2) {
    const q = QUIZ[quizStep];
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
        style={{ background: 'linear-gradient(135deg,#041e2b,#094f6f)' }}>
        <div className="w-full max-w-lg">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-white/50 mb-2">
              <span>VibeMate Quiz · Question {quizStep + 1} of {QUIZ.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-1.5">
              <div className="h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg,#2cbdc4,white)' }} />
            </div>
          </div>

          <div className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
            <h2 className="text-2xl font-black text-white text-center mb-8">{q.q}</h2>
            <div className="space-y-3">
              {q.opts.map((opt, i) => (
                <button key={i} onClick={() => !saving && handleAnswer(i)}
                  disabled={saving}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/15 hover:border-[#2cbdc4]/60 transition-all group">
                  <span className="text-3xl">{opt.icon}</span>
                  <div className="text-left">
                    <p className="font-bold text-white">{opt.label}</p>
                    <p className="text-xs text-white/50">{opt.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {quizStep > 0 && (
            <button onClick={() => { setAnswers(a => a.slice(0, -1)); setQuizStep(q => q - 1); }}
              className="mt-4 mx-auto block text-xs text-white/40 hover:text-white/70 transition-colors">
              ← Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Step 3: Profile ready ─────────────────────────────────────────────────
  const avatarUrl = VIBE_TYPE_AVATARS[vibeType];
  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#041e2b,#083a54)' }}>
      <div className="w-full max-w-md text-center">
        <div className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-3xl p-10">
          <div className="w-28 h-28 rounded-3xl overflow-hidden mx-auto mb-6 border-2 border-[#2cbdc4]/50">
            {avatarUrl
              ? <img src={avatarUrl} alt={vibeType} className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-[#2cbdc4]/20 flex items-center justify-center text-5xl">🎉</div>
            }
          </div>
          <div className="inline-flex items-center gap-1.5 bg-[#2cbdc4]/15 border border-[#2cbdc4]/30 px-4 py-1.5 rounded-full text-xs font-bold text-[#2cbdc4] mb-4">
            Your VibeMate Profile is Ready ✓
          </div>
          <h2 className="text-3xl font-black text-white mb-2">
            {VIBE_LABELS[vibeType] || 'The Free Spirit'}
          </h2>
          <p className="text-white/50 text-sm mb-8">Your lifestyle profile is now set. We&apos;ll use this to find compatible housemates and properties.</p>

          {/* Points animation */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-8">
            <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Points Awarded</p>
            <p className="text-5xl font-black text-[#2cbdc4]">+{animPts}</p>
            <p className="text-white/40 text-xs mt-1">VibeMate quiz completion bonus</p>
          </div>

          <button onClick={() => navigate('/student-dashboard')}
            className="w-full py-4 rounded-2xl font-bold text-white text-base hover:opacity-90 transition-opacity"
            style={{ background: BRAND }}>
            Start exploring →
          </button>
        </div>
      </div>
    </div>
  );
}
